import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Visibility, VisibilityOff, Print, FileDownload } from "@mui/icons-material";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ReportSiblingNav from "../components/Reports/ReportSiblingNav";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import ReportSummaryCard, { summaryCardsRowSx } from "../components/Reports/ReportSummaryCard";
import { FooterInternal } from "../components/Footer";
import MultiLineChart, { ChartSeries } from "../components/charts/MultiLineChart";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { patientFlowReportService, PatientFlowResponse } from "../services/reportsService";
import { colors, typography } from "../theme/designSystem";
import { filterFieldSx } from "../theme/filterStyles";
import { maskName } from "../utils/nameMask";
import MonthSelect from "../components/Inputs/MonthSelect";

// Novos Pacientes e Evasão (30 dias) — RN-211. Fonte: espelho do Bubble via API.
// Novo = 1º atendimento realizado da história na competência; evasão = 30 dias sem
// frequentar completados na competência (fluxo, não foto); retorno = voltou após
// mais de 30 dias parado. "Frequentar" = presença "Compareceu".

const currentCompetence = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const formatMonthLabel = (month: string): string => {
  const [year, monthNumber] = month.split("-");
  const names = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  return `${names[parseInt(monthNumber, 10) - 1]}/${year.slice(2)}`;
};


const sectionStyle = {
  background: "#fff",
  borderRadius: 2,
  border: "1px solid #e9ecef",
  p: 2,
  mb: 3,
};

const NEW_COLOR = "#28a745";
const CHURN_COLOR = "#dc3545";
const RETURN_COLOR = "#f39c12";

const ReportPatientFlow: React.FC = () => {
  const { navigateTo, getParam } = useRouter();
  const { user } = useAuth();


  // competência vinda da Análise de Atendimentos (navegação preserva o período)
  const [competence, setCompetence] = useState<string>(getParam("competence") || currentCompetence());
  const [showNames, setShowNames] = useState<boolean>(false);

  const [data, setData] = useState<PatientFlowResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const displayName = (name: string): string => (showNames ? name : maskName(name));

  const hasFilterChanges = competence !== currentCompetence();

  const loadData = useCallback(async (month?: string) => {
    const value = month ?? competence;
    if (!value) return;
    setLoading(true);
    setError("");
    try {
      const response = await patientFlowReportService.get(value);
      setData(response);
    } catch {
      setError("Não foi possível carregar os dados. Tente novamente.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competence]);

  const handleCompetenceChange = (value: string) => {
    setCompetence(value);
    if (value) loadData(value);
  };

  const clearFilters = () => {
    const value = currentCompetence();
    setCompetence(value);
    loadData(value);
  };

  const handleExport = async () => {
    setExporting(true);
    setError("");
    try {
      const blob = await patientFlowReportService.exportXlsx(competence);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `novos_evasao_${competence}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Não foi possível exportar a planilha. Tente novamente.");
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = () => {
    // impressão sempre com nomes mascarados, independentemente do estado da tela
    const wasShowing = showNames;
    setShowNames(false);
    setTimeout(() => {
      window.print();
      setShowNames(wasShowing);
    }, 150);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const labels = data?.monthly.map((m) => m.month) ?? [];

  const chartSeries: ChartSeries[] = data
    ? [
        { name: "Novos", color: NEW_COLOR, values: data.monthly.map((m) => m.newCount) },
        { name: "Evasão 30d", color: CHURN_COLOR, values: data.monthly.map((m) => m.churnCount) },
        { name: "Retornos", color: RETURN_COLOR, values: data.monthly.map((m) => m.returnCount) },
      ]
    : [];

  const totalsCards = data
    ? [
        { label: "Novos no mês", value: String(data.summary.newCount), color: NEW_COLOR },
        {
          label: "Evasão no mês (30d)",
          value: String(data.summary.churnCount),
          color: CHURN_COLOR,
          note: data.isPartialMonth ? "mês parcial: evasões em aberto ficam fora" : undefined,
        },
        { label: "Retornos no mês", value: String(data.summary.returnCount), color: RETURN_COLOR },
        {
          label: "Saldo do mês",
          value: `${data.summary.netBalance >= 0 ? "+" : ""}${data.summary.netBalance}`,
          color: data.summary.netBalance >= 0 ? NEW_COLOR : CHURN_COLOR,
          sub: "novos + retornos − evasão",
        },
        { label: "Pacientes atendidos", value: String(data.summary.attendedPatients), color: colors.primary },
      ]
    : [];

  const hasAnyData =
    !!data &&
    (data.summary.attendedPatients > 0 || data.monthly.some((m) => m.newCount + m.churnCount + m.returnCount > 0));

  const rowStyle = {
    display: "flex",
    alignItems: "center",
    gap: 1,
    py: 0.75,
    borderBottom: "1px solid #f1f3f5",
    flexWrap: "nowrap" as const,
  };

  const headerRowStyle = {
    ...rowStyle,
    borderBottom: "2px solid #dee2e6",
    color: "#6c757d",
  };

  const churnSituation = (returnedDate?: string | null, registerRemoved?: boolean): string => {
    if (returnedDate) return `Retornou em ${returnedDate}`;
    return registerRemoved ? "Cadastro removido" : "Sem retorno";
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <style>{`
        @media print {
          .login-header, .login-footer-component, .no-print { display: none !important; }
          body { background: #fff !important; }
          main { margin-top: 0 !important; padding: 0 !important; background: #fff !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          /* tabelas largas: paisagem e sem scroll horizontal, para nada sair cortado */
          @page { size: A4 landscape; margin: 8mm; }
          [style*="overflow-x"], [style*="overflowX"] { overflow: visible !important; }
          table { min-width: 0 !important; width: 100% !important; font-size: 10px !important; }
          thead { display: table-header-group; }
          tr { break-inside: avoid; }
        }
      `}</style>

      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters>
          <Box
            sx={{
              mb: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
              <Typography variant="h4" className="c4u-page-header__title">
                Relatórios / Novos Pacientes e Evasão (30 dias)
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Fonte: agenda (sincronizada de hora em hora). Novo = primeiro atendimento realizado na
                competência; evasão = 30 dias sem frequentar completados na competência; retorno = voltou
                após mais de 30 dias parado.
              </Typography>
            </Box>
            <Box className="no-print">
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
                <MonthSelect
                  label="Competência"
                  value={competence}
                  onChange={handleCompetenceChange}
                  sx={filterFieldSx}
                />
              </div>
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges || loading} />
                <Tooltip title={showNames ? "Ocultar nomes" : "Exibir nomes"}>
                  <span>
                    <IconButton
                      className="c4u-btn-icon c4u-btn-icon--outline"
                      onClick={() => setShowNames((v) => !v)}
                      aria-label={showNames ? "Ocultar nomes" : "Exibir nomes"}
                    >
                      {showNames ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Imprimir (nomes sempre mascarados)">
                  <span>
                    <IconButton
                      className="c4u-btn-icon c4u-btn-icon--outline"
                      onClick={handlePrint}
                      disabled={loading || !data}
                      aria-label="Imprimir"
                    >
                      <Print fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Exportar planilha de novos e evasão">
                  <span>
                    <IconButton
                      className="c4u-btn-icon c4u-btn-icon--outline"
                      onClick={handleExport}
                      disabled={exporting || loading}
                      aria-label="Exportar planilha"
                    >
                      {exporting ? <CircularProgress size={16} sx={{ color: colors.primary }} /> : <FileDownload fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>

            <ReportSiblingNav current="report-patient-flow" competence={competence || currentCompetence()} />

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          )}

          {!loading && error && (
            <Box sx={{ ...sectionStyle, textAlign: "center", color: "#dc3545" }}>{error}</Box>
          )}

          {!loading && !error && data && !hasAnyData && (
            <Box sx={{ ...sectionStyle, textAlign: "center", color: "#6c757d" }}>
              Nenhuma movimentação de pacientes encontrada para a competência selecionada.
            </Box>
          )}

          {!loading && !error && data && hasAnyData && (
            <>
              {/* Cards de resumo */}
              <Box sx={summaryCardsRowSx}>
                {totalsCards.map((card) => (
                  <ReportSummaryCard key={card.label} {...card} />
                ))}
              </Box>

              {/* Evolutivo 12 meses */}
              <Box sx={sectionStyle}>
                <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 0.5 }}>
                  Evolutivo — últimos 12 meses
                </Typography>
                {data.isPartialMonth && (
                  <Typography sx={{ fontSize: 12, color: "#6c757d", mb: 1.5 }}>
                    A competência corrente é parcial: evasões que ainda não completaram 30 dias não aparecem.
                  </Typography>
                )}
                <MultiLineChart labels={labels} series={chartSeries} showValues />

                <Box sx={{ overflowX: "auto", mt: 2 }}>
                  <Box sx={{ minWidth: 740 }}>
                    <Box sx={headerRowStyle}>
                      <Typography sx={{ flex: 1, fontSize: 12, minWidth: 64 }}>Mês</Typography>
                      <Typography sx={{ width: 80, textAlign: "right", fontSize: 12 }}>Novos</Typography>
                      <Typography sx={{ width: 76, textAlign: "right", fontSize: 12 }}>bebês (&lt;1a)</Typography>
                      <Typography sx={{ width: 80, textAlign: "right", fontSize: 12 }}>Evasão 30d</Typography>
                      <Typography sx={{ width: 76, textAlign: "right", fontSize: 12 }}>bebês (&lt;1a)</Typography>
                      <Typography sx={{ width: 80, textAlign: "right", fontSize: 12 }}>Retornos</Typography>
                      <Typography sx={{ width: 80, textAlign: "right", fontSize: 12 }}>Saldo</Typography>
                      <Typography sx={{ width: 96, textAlign: "right", fontSize: 12 }}>Saldo s/ bebês</Typography>
                    </Box>
                    {data.monthly.map((m) => {
                      const balance = m.newCount + m.returnCount - m.churnCount;
                      const balanceNoBabies =
                        m.newCount - m.newBabiesCount + m.returnCount - (m.churnCount - m.churnBabiesCount);
                      return (
                        <Box key={m.month} sx={rowStyle}>
                          <Typography sx={{ flex: 1, fontSize: 14, minWidth: 64 }}>
                            {formatMonthLabel(m.month)}
                          </Typography>
                          <Typography sx={{ width: 80, textAlign: "right", fontSize: 14, color: NEW_COLOR }}>
                            {m.newCount}
                          </Typography>
                          <Typography sx={{ width: 76, textAlign: "right", fontSize: 13, color: "#6c757d" }}>
                            {m.newBabiesCount}
                          </Typography>
                          <Typography sx={{ width: 80, textAlign: "right", fontSize: 14, color: CHURN_COLOR }}>
                            {m.churnCount}
                          </Typography>
                          <Typography sx={{ width: 76, textAlign: "right", fontSize: 13, color: "#6c757d" }}>
                            {m.churnBabiesCount}
                          </Typography>
                          <Typography sx={{ width: 80, textAlign: "right", fontSize: 14, color: RETURN_COLOR }}>
                            {m.returnCount}
                          </Typography>
                          <Typography
                            sx={{
                              width: 80,
                              textAlign: "right",
                              fontSize: 14,
                              fontWeight: typography.fontWeight.semibold,
                              color: balance >= 0 ? NEW_COLOR : CHURN_COLOR,
                            }}
                          >
                            {balance >= 0 ? `+${balance}` : balance}
                          </Typography>
                          <Typography
                            sx={{
                              width: 96,
                              textAlign: "right",
                              fontSize: 14,
                              fontWeight: typography.fontWeight.semibold,
                              color: balanceNoBabies >= 0 ? NEW_COLOR : CHURN_COLOR,
                            }}
                          >
                            {balanceNoBabies >= 0 ? `+${balanceNoBabies}` : balanceNoBabies}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
                <Typography sx={{ fontSize: 12, color: "#6c757d", mt: 1 }}>
                  As colunas "bebês (&lt;1a)" destacam pacientes com menos de 1 ano na data do evento
                  (avaliações de poucas sessões) e já estão incluídas nos totais de novos e evasão.
                  "Saldo s/ bebês" desconta os bebês dos dois lados: (novos − bebês) + retornos − (evasão − bebês).
                </Typography>
              </Box>

              {/* Listas da competência (empilhadas — largura total) */}
              <Box sx={sectionStyle}>
                  <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 1 }}>
                    Pacientes novos na competência ({data.newPatients.length})
                  </Typography>
                  {data.newPatients.length === 0 && (
                    <Typography sx={{ fontSize: 13, color: "#6c757d" }}>
                      Nenhum paciente novo na competência.
                    </Typography>
                  )}
                  {data.newPatients.length > 0 && (
                    <Box sx={{ overflowX: "auto" }}>
                      <Box sx={{ minWidth: 820 }}>
                        <Box sx={headerRowStyle}>
                          <Typography sx={{ flex: 2, fontSize: 12, minWidth: 140 }}>Paciente</Typography>
                          <Typography sx={{ width: 46, textAlign: "right", fontSize: 12 }}>Idade</Typography>
                          <Typography sx={{ width: 92, textAlign: "right", fontSize: 12 }}>1º atendimento</Typography>
                          <Typography sx={{ flex: 1.5, fontSize: 12, minWidth: 110, pl: 1 }}>Profissional</Typography>
                          <Typography sx={{ flex: 1.5, fontSize: 12, minWidth: 150, pl: 1 }}>Serviço</Typography>
                          <Typography sx={{ width: 70, textAlign: "right", fontSize: 12 }}>No mês</Typography>
                          <Typography sx={{ width: 100, fontSize: 12, pl: 1 }}>Origem</Typography>
                        </Box>
                        {data.newPatients.map((p) => (
                          <Box key={p.patientExternalId} sx={rowStyle}>
                            <Typography sx={{ flex: 2, fontSize: 14, minWidth: 140 }}>
                              {displayName(p.patientName)}
                            </Typography>
                            <Typography sx={{ width: 46, textAlign: "right", fontSize: 14 }}>
                              {p.age ?? "—"}
                            </Typography>
                            <Typography sx={{ width: 92, textAlign: "right", fontSize: 14 }}>
                              {p.firstVisitDate}
                            </Typography>
                            <Typography sx={{ flex: 1.5, fontSize: 13, minWidth: 110, pl: 1, color: "#6c757d" }}>
                              {displayName(p.professionalName)}
                            </Typography>
                            <Typography sx={{ flex: 1.5, fontSize: 13, minWidth: 150, pl: 1, color: "#6c757d" }}>
                              {p.serviceType}
                            </Typography>
                            <Typography sx={{ width: 70, textAlign: "right", fontSize: 14 }}>
                              {p.visitsInMonth}
                            </Typography>
                            <Typography sx={{ width: 100, fontSize: 13, pl: 1, color: "#6c757d" }}>
                              {p.registerOrigin || "—"}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>

                <Box sx={sectionStyle}>
                  <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 1 }}>
                    Pacientes em evasão na competência ({data.churnPatients.length})
                  </Typography>
                  {data.churnPatients.length === 0 && (
                    <Typography sx={{ fontSize: 13, color: "#6c757d" }}>
                      Nenhum paciente completou 30 dias sem frequentar na competência.
                    </Typography>
                  )}
                  {data.churnPatients.length > 0 && (
                    <Box sx={{ overflowX: "auto" }}>
                      <Box sx={{ minWidth: 880 }}>
                        <Box sx={headerRowStyle}>
                          <Typography sx={{ flex: 2, fontSize: 12, minWidth: 140 }}>Paciente</Typography>
                          <Typography sx={{ width: 46, textAlign: "right", fontSize: 12 }}>Idade</Typography>
                          <Typography sx={{ width: 96, textAlign: "right", fontSize: 12 }}>Último atend.</Typography>
                          <Typography sx={{ width: 70, textAlign: "right", fontSize: 12 }}>Dias sem vir</Typography>
                          <Typography sx={{ flex: 1.5, fontSize: 12, minWidth: 110, pl: 1 }}>Profissional</Typography>
                          <Typography sx={{ flex: 1.5, fontSize: 12, minWidth: 150, pl: 1 }}>Serviço</Typography>
                          <Typography sx={{ width: 150, fontSize: 12, pl: 1 }}>Situação</Typography>
                        </Box>
                        {data.churnPatients.map((p) => (
                          <Box key={`${p.patientExternalId}-${p.lastVisitDate}`} sx={rowStyle}>
                            <Typography sx={{ flex: 2, fontSize: 14, minWidth: 140 }}>
                              {displayName(p.patientName)}
                            </Typography>
                            <Typography sx={{ width: 46, textAlign: "right", fontSize: 14 }}>
                              {p.age ?? "—"}
                            </Typography>
                            <Typography sx={{ width: 96, textAlign: "right", fontSize: 14 }}>
                              {p.lastVisitDate}
                            </Typography>
                            <Typography sx={{ width: 70, textAlign: "right", fontSize: 14, color: CHURN_COLOR }}>
                              {p.daysWithoutVisit}
                            </Typography>
                            <Typography sx={{ flex: 1.5, fontSize: 13, minWidth: 110, pl: 1, color: "#6c757d" }}>
                              {displayName(p.professionalName)}
                            </Typography>
                            <Typography sx={{ flex: 1.5, fontSize: 13, minWidth: 150, pl: 1, color: "#6c757d" }}>
                              {p.serviceType}
                            </Typography>
                            <Typography
                              sx={{
                                width: 150,
                                fontSize: 13,
                                pl: 1,
                                color: p.returnedDate ? NEW_COLOR : "#6c757d",
                              }}
                            >
                              {churnSituation(p.returnedDate, p.registerRemoved)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
            </>
          )}
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default ReportPatientFlow;
